import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { analyticsApi } from '../../api/analytics.api';
import {
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  UsersIcon,
  CpuChipIcon,
  MicrophoneIcon
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

function StatCard({ title, value, subtitle, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
}

const COLORS = ['#3ee851', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

export default function AnalyticsOverviewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await analyticsApi.getOverview(days);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching analytics overview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [days]);

  const formatCost = (value) => `$${(value || 0).toFixed(4)}`;
  const formatNumber = (value) => (value || 0).toLocaleString();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3ee851]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">OpenAI Analytics</h1>
          <p className="text-gray-600">Monitor API usage, costs, and performance</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#3ee851] focus:border-transparent"
        >
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Cost"
          value={formatCost(data?.summary?.totalCost)}
          subtitle={`${days} days`}
          icon={CurrencyDollarIcon}
          color="bg-green-500"
        />
        <StatCard
          title="Total Requests"
          value={formatNumber(data?.summary?.totalRequests)}
          icon={ChatBubbleLeftRightIcon}
          color="bg-blue-500"
        />
        <StatCard
          title="Success Rate"
          value={`${data?.summary?.successRate || 0}%`}
          icon={CheckCircleIcon}
          color="bg-emerald-500"
        />
        <StatCard
          title="Active Users"
          value={formatNumber(data?.summary?.activeUsers)}
          icon={UsersIcon}
          color="bg-purple-500"
        />
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Link
          to="/analytics/costs"
          className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow flex items-center gap-3"
        >
          <CurrencyDollarIcon className="h-8 w-8 text-green-500" />
          <div>
            <p className="font-medium text-gray-900">Cost Tracking</p>
            <p className="text-sm text-gray-500">View detailed cost data</p>
          </div>
        </Link>
        <Link
          to="/analytics/logs"
          className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow flex items-center gap-3"
        >
          <CpuChipIcon className="h-8 w-8 text-blue-500" />
          <div>
            <p className="font-medium text-gray-900">API Logs</p>
            <p className="text-sm text-gray-500">Browse request logs</p>
          </div>
        </Link>
        <Link
          to="/analytics/rate-limits"
          className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow flex items-center gap-3"
        >
          <ExclamationCircleIcon className="h-8 w-8 text-orange-500" />
          <div>
            <p className="font-medium text-gray-900">Rate Limits</p>
            <p className="text-sm text-gray-500">Monitor rate limiting</p>
          </div>
        </Link>
        <Link
          to="/analytics/audios"
          className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow flex items-center gap-3"
        >
          <MicrophoneIcon className="h-8 w-8 text-purple-500" />
          <div>
            <p className="font-medium text-gray-900">Audios</p>
            <p className="text-sm text-gray-500">Audio transcriptions</p>
          </div>
        </Link>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Daily Cost Trend */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Cost Trend</h3>
          {data?.dailyCosts?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.dailyCosts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="_id"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis tickFormatter={(value) => `$${value.toFixed(2)}`} />
                <Tooltip
                  formatter={(value) => [`$${value.toFixed(4)}`, 'Cost']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString()}
                />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke="#3ee851"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No cost data available
            </div>
          )}
        </div>

        {/* Model Usage Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Model Usage</h3>
          {data?.modelBreakdown?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.modelBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="_id"
                >
                  {data.modelBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [value, 'Requests']}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              No model data available
            </div>
          )}
        </div>
      </div>

      {/* Top Users by Cost */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Users by Cost</h3>
        {data?.topUsers?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requests
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.topUsers.map((user, index) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-white ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <p className="font-medium text-gray-900">{user.fullName || user.username}</p>
                        <p className="text-sm text-gray-500">@{user.username}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${user.totalCost?.toFixed(4)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.totalRequests?.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-gray-500 py-8">
            No user data available
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
